import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { LogOut, Plus, Users, TrendingUp, AlertTriangle, XCircle, FileText, Trash2 } from 'lucide-react';
import Sidebar from '../components/Sidebar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  'Normal': 'bg-green-100 text-green-800',
  'Alerta Amarelo': 'bg-yellow-100 text-yellow-800',
  'Alerta Vermelho': 'bg-red-100 text-red-800',
  'Descontinuado': 'bg-gray-100 text-gray-800'
};

export default function DashboardGestor({ user, onLogout }) {
  const navigate = useNavigate();
  const [painelData, setPainelData] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newCorretor, setNewCorretor] = useState({
    nome: '',
    email: '',
    data_inicio: new Date().toISOString().split('T')[0],
    mentor_id: '',
    mentor_nome: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const handleExportExcel = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/corretores/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `corretores_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Relatório exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar relatório');
      console.error(error);
    }
  };

  const handleImportExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Apenas arquivos Excel (.xlsx, .xls) são permitidos');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/corretores/import/excel`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`Importação concluída! ${response.data.added} adicionados, ${response.data.updated} atualizados`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar arquivo');
      console.error(error);
    }

    event.target.value = '';
  };

  const loadData = async () => {
    try {
      const [painelRes, statsRes] = await Promise.all([
        axios.get(`${API}/painel`),
        axios.get(`${API}/dashboard/stats`)
      ]);
      setPainelData(painelRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCorretor = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/corretores`, newCorretor);
      toast.success('Corretor cadastrado com sucesso!');
      setIsDialogOpen(false);
      setNewCorretor({
        nome: '',
        email: '',
        data_inicio: new Date().toISOString().split('T')[0],
        mentor_id: '',
        mentor_nome: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar corretor');
    }
  };

  // ⭐ NOVA FUNÇÃO: Excluir corretor
  const handleDeleteCorretor = async (corretorId, corretorNome) => {
    if (!window.confirm(`Tem certeza que deseja excluir o corretor ${corretorNome}? Esta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/corretores/${corretorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Corretor excluído com sucesso!');
      loadData();
    } catch (error) {
      console.error('Erro ao excluir corretor:', error);
      toast.error('Erro ao excluir corretor');
    }
  };

  const filteredData = painelData.filter(item =>
    item.corretor.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.corretor.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-remax-blue"></div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#F3F4F6' }}>
      <Sidebar currentPage="dashboard" user={user} onLogout={onLogout} />
      
      <div style={{ flex: 1, overflow: 'auto' }}>
        {/* Header */}
        <header className="bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center space-x-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Painel do Gestor</h1>
                  <p className="text-sm text-gray-500">Acompanhamento de corretores e desempenho</p>
                </div>
              </div>
            </div>
          </div>
        </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card data-testid="stat-card-total">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Corretores</CardTitle>
                <Users className="h-4 w-4 text-remax-blue" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold" style={{ color: '#003DA5' }}>{stats.total_corretores}</div>
                <p className="text-xs text-gray-500 mt-1">No programa</p>
              </CardContent>
            </Card>

            <Card data-testid="stat-card-captacoes">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Captações Total</CardTitle>
                <TrendingUp className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.captacoes_total}</div>
                <p className="text-xs text-gray-500 mt-1">Média: {stats.media_captacoes_por_corretor}</p>
              </CardContent>
            </Card>

            <Card data-testid="stat-card-alertas">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Alertas</CardTitle>
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">
                  {stats.corretores_alerta_amarelo + stats.corretores_alerta_vermelho}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Amarelo: {stats.corretores_alerta_amarelo} | Vermelho: {stats.corretores_alerta_vermelho}
                </p>
              </CardContent>
            </Card>

            <Card data-testid="stat-card-descontinuados">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Descontinuados</CardTitle>
                <XCircle className="h-4 w-4 text-gray-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-600">{stats.corretores_descontinuados}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Painel Geral */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
              <CardTitle style={{ fontFamily: 'Work Sans', color: '#003DA5' }}>
                PAINEL GERAL - ONBOARDING 90 DIAS
              </CardTitle>
              <div className="flex space-x-2">
                <Input
                  placeholder="Buscar corretor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-64"
                  data-testid="search-input"
                />
                <Button 
                  onClick={handleExportExcel}
                  variant="outline"
                  className="bg-green-50 hover:bg-green-100 border-green-300"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Exportar Relatório
                </Button>
                <label>
                  <Button 
                    variant="outline"
                    className="bg-blue-50 hover:bg-blue-100 border-blue-300"
                    onClick={() => document.getElementById('import-excel-input-gestor').click()}
                    type="button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Importar Relatório
                  </Button>
                  <input
                    id="import-excel-input-gestor"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleImportExcel}
                    style={{ display: 'none' }}
                  />
                </label>
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-remax-blue hover:bg-[#002C7A]" data-testid="add-corretor-button">
                      <Plus className="w-4 h-4 mr-2" />
                      Novo Corretor
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Cadastrar Novo Corretor</DialogTitle>
                      <DialogDescription>Preencha os dados do corretor para iniciar o onboarding</DialogDescription>
                    </DialogHeader>
    
                   <form onSubmit={handleCreateCorretor} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="nome">Nome Completo</Label>
                        <Input
                          id="nome"
                          value={newCorretor.nome}
                          onChange={(e) => setNewCorretor({ ...newCorretor, nome: e.target.value })}
                          required
                          data-testid="corretor-nome-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          value={newCorretor.email}
                          onChange={(e) => setNewCorretor({ ...newCorretor, email: e.target.value })}
                          required
                          data-testid="corretor-email-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="data_inicio">Data de Início</Label>
                        <Input
                          id="data_inicio"
                          type="date"
                          value={newCorretor.data_inicio}
                          onChange={(e) => setNewCorretor({ ...newCorretor, data_inicio: e.target.value })}
                          required
                          data-testid="corretor-data-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="mentor_nome">Nome do Mentor/Broker</Label>
                        <Input
                          id="mentor_nome"
                          value={newCorretor.mentor_nome}
                          onChange={(e) => setNewCorretor({ ...newCorretor, mentor_nome: e.target.value })}
                          placeholder="Opcional"
                          data-testid="corretor-mentor-input"
                        />
                      </div>
                      <Button type="submit" className="w-full bg-remax-blue hover:bg-[#002C7A]" data-testid="submit-corretor-button">
                        Cadastrar
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="painel-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Corretor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Início</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Semana</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Módulo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mentor</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">CRE</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Estudos de Mercado</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Contatos</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Visitas de Captação</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Presença na Silver</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">C2S</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">PipeImob</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">iList</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">BrokerApp</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredData.map((item, index) => (
                    <tr key={item.corretor.id} className="hover:bg-gray-50" data-testid={`corretor-row-${index}`}>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div>
                          {/* ⭐ NOME CLICÁVEL */}
                          <div 
                            className="text-sm font-medium cursor-pointer"
                            style={{ color: '#1D4ED8' }}
                            onClick={() => navigate(`/ficha-semanal/${item.corretor.id}`)}
                            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                          >
                            {item.corretor.nome}
                          </div>
                          <div className="text-sm text-gray-500">{item.corretor.email}</div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(item.corretor.data_inicio).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center text-sm font-medium">
                        {item.corretor.semana_atual}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center text-sm font-medium">
                        {item.corretor.modulo_atual}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.corretor.mentor_nome || '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center text-sm font-semibold text-green-600">
                        {item.corretor.captacoes_acumuladas}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center text-sm">
                        {item.corretor.estudos_mercado_feitos}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center text-sm">
                        {item.ligacoes_ultima_semana}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center text-sm">
                        {item.visitas_1_ultima_semana + item.visitas_2_ultima_semana}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center text-sm">
                        {item.presenca_dias}/7
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center text-sm">
                        {item.uso_c2s ? '✓' : '✗'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center text-sm">
                        {item.uso_pipeimob ? '✓' : '✗'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center text-sm">
                        {item.uso_ilist ? '✓' : '✗'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center text-sm">
                        {item.uso_brokerapp ? '✓' : '✗'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColors[item.corretor.status]}`}>
                          {item.corretor.status}
                        </span>
                      </td>
                      {/* ⭐ COLUNA AÇÕES COM BOTÃO EXCLUIR */}
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/ficha-semanal/${item.corretor.id}`)}
                            data-testid={`view-scorecard-${index}`}
                            title="Ver ficha semanal"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCorretor(item.corretor.id, item.corretor.nome)}
                            className="text-red-600 hover:bg-red-50 hover:text-red-700"
                            data-testid={`delete-corretor-${index}`}
                            title="Excluir corretor"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredData.length === 0 && (
                <div className="text-center py-12 text-gray-500" data-testid="no-data-message">
                  Nenhum corretor encontrado
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
      </div>
    </div>
  );
}
